import {
  canApproveProposal,
  describeMachineReason,
  formatSearchVolume,
  isCostAvoidanceExclusion,
  isTgAssignmentAssign,
  type MachineProposal,
} from "../data/cockpit-data";
import { StatTile } from "./StatTile";

function signed(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

/**
 * The detail band of an expanded proposal: search volume, units effect, profit
 * effect, and why the machine proposed it.
 *
 * Meteoric painted the profit figure green when positive and amber when
 * negative. Here the figure is plain and the SENTENCE under it carries the
 * meaning — a negative profit effect on an exclusion is not a warning, it is
 * the point of the action, and a colour cannot say that. The one thing the
 * original said in colour that mattered ("we could not compute this") is now
 * said in words on the same tile.
 */
export function StatsBand({ proposal }: { proposal: MachineProposal }) {
  const searchVolume = formatSearchVolume(proposal.search_volume);
  const ready = canApproveProposal(proposal);
  // profit_effect is stamped once at proposal creation and never recomputed. For
  // exclusions it is null BY DESIGN (the win is avoided waste, not forecast profit),
  // so a null is a permanent fact to state, never a spinner.
  const costAvoidance = isCostAvoidanceExclusion(proposal) && proposal.profit_effect === null;
  const assignment = isTgAssignmentAssign(proposal);
  const profitValue = assignment
    ? "No forecast"
    : proposal.profit_effect !== null
      ? signed(proposal.profit_effect)
      : costAvoidance
        ? "No forecast"
        : "Not computed";
  const profitNote = costAvoidance
    ? "Cost-avoidance action: the win is cutting wasted spend. Exclusions never carry a profit forecast."
    : assignment
      ? "Assignments carry no profit forecast. Your choice of target group is the judgment."
      : !ready
        ? "Can't compute impact, not safe to approve."
        : null;

  const explained = describeMachineReason(proposal.reason);

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[0.8fr_1fr_1fr_1.6fr]">
      <StatTile label="Search volume">
        {searchVolume ? (
          <>
            <div className="text-xl font-semibold tabular-nums text-ks-ink">{searchVolume}</div>
            <div className="mt-1 text-xs text-muted-foreground">searches / month</div>
          </>
        ) : (
          <div className="text-[13px] font-medium text-muted-foreground">Not tracked</div>
        )}
      </StatTile>

      <StatTile label="Units effect">
        <div className="text-[15px] font-semibold tabular-nums text-ks-ink">
          {proposal.units_effect ?? "Impact pending"}
        </div>
      </StatTile>

      <StatTile label="Profit-yield effect">
        <div className="text-xl font-semibold tabular-nums text-ks-ink">{profitValue}</div>
        {profitNote && <div className="mt-1 text-xs text-muted-foreground">{profitNote}</div>}
      </StatTile>

      <StatTile label="Why the machine proposed this">
        <p className="text-[13px] leading-relaxed text-ks-ink">
          {explained
            ? `${explained.purpose ? `${explained.purpose}: ` : ""}${explained.summary}`
            : "Analysis unavailable."}
        </p>
        {explained?.code && (
          <div className="mt-2 font-mono text-[11px] text-muted-foreground">Audit code: {explained.code}</div>
        )}
        {proposal.confidence !== null && (
          <div className="mt-2 text-xs font-medium tabular-nums text-muted-foreground">
            Confidence {(proposal.confidence * 100).toFixed(0)}%
          </div>
        )}
      </StatTile>
    </div>
  );
}
